import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWifi, faExclamationTriangle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import websocketService from "../../../function/websocketService";

const WebSocketStatus = React.memo(() => {
  const [status, setStatus] = useState({
    isConnected: false,
    connectionAttempts: 0,
    lastError: null,
  });

  useEffect(() => {
    const handleWebSocketEvent = (eventType, data) => {
      switch (eventType) {
        case 'connected':
          setStatus({
            isConnected: true,
            connectionAttempts: 0,
            lastError: null,
          });
          break;

        case 'disconnected':
          setStatus(prev => ({ ...prev, isConnected: false }));
          break;

        case 'error':
          setStatus(prev => ({ ...prev, isConnected: false, lastError: data }));
          break;

        case 'maxReconnectAttemptsReached':
          setStatus(prev => ({
            ...prev,
            isConnected: false,
            connectionAttempts: data.attempts,
          }));
          break;

        default:
          break;
      }
    };

    const unsubscribe = websocketService.onMessage(handleWebSocketEvent);

    const initialStatus = websocketService.getConnectionStatus();
    setStatus({
      isConnected: initialStatus.isConnected,
      connectionAttempts: initialStatus.connectionAttempts,
      lastError: null,
    });

    return unsubscribe;
  }, []);

  const getStatusColor = () => {
    if (status.isConnected) return '#28a745';
    if (status.connectionAttempts > 0) return '#ffc107';
    return '#dc3545';
  };

  const getStatusText = () => {
    if (status.isConnected) return 'Connected';
    if (status.connectionAttempts > 0) return `Reconnecting (${status.connectionAttempts})`;
    return 'Disconnected';
  };

  const getStatusIcon = () => {
    if (status.isConnected) return faWifi;
    if (status.connectionAttempts > 0) return faSpinner;
    return faExclamationTriangle;
  };

  // Only show the indicator when not connected so it doesn't clutter the UI
  if (status.isConnected) return null;

  return (
    <div
      className="d-flex align-items-center"
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        zIndex: 1000,
        backdropFilter: 'blur(10px)',
      }}
      title={
        status.lastError
          ? `Error: ${status.lastError.message || 'Connection failed'}`
          : getStatusText()
      }
      role="status"
      aria-live="polite"
    >
      <FontAwesomeIcon
        icon={getStatusIcon()}
        style={{
          color: getStatusColor(),
          marginRight: '6px',
          fontSize: '10px',
        }}
        className={status.connectionAttempts > 0 ? 'fa-spin' : ''}
      />
      <span style={{ fontSize: '11px' }}>{getStatusText()}</span>
    </div>
  );
});

export default WebSocketStatus;
